"use client";

import { ErrorBoundary } from "@/components/error-boundary";
import { Tab, TabList, TabPanel, Tabs } from "@/components/rac/tabs";
import { SUPCard } from "@/components/sup/card";
import { usePropertyInteractionStore } from "@/stores/property-interaction";
import { type SellerUnitsAndProjectsOne } from "@/types/sup";
import React, { useMemo, useState } from "react";

interface AvailableSUPsProps {
	sups: SellerUnitsAndProjectsOne[];
}

const ProjectCardFallback = ({ children }: { children?: React.ReactNode }) => (
	<div className="w-full p-6 border border-red-200 rounded-lg bg-red-50 relative">
		<div className="text-center">
			<div className="text-red-800 font-semibold mb-2">Project Cards Error</div>
			<p className="text-red-600 text-sm">Unable to load available properties</p>
		</div>
		{children}
	</div>
);

export function AvailableSUPs({ sups }: AvailableSUPsProps) {
	const [selectedTab, setSelectedTab] = useState<string>("");
	const openPropertyOverlay = usePropertyInteractionStore(state => state.openPropertyOverlay);

	const groupedSUPs = useMemo(() => {
		if (!sups || sups.length === 0) return {};

		const groups: Record<string, SellerUnitsAndProjectsOne[]> = {};

		sups.forEach(sup => {
			const bedroomCounts = sup.bedrooms || [];
			if (bedroomCounts.length === 0) {
				// Handle SUPs without bedroom info
				const key = "Other";
				if (!groups[key]) groups[key] = [];
				groups[key].push(sup);
			} else {
				// Add unit to each BHK group it belongs to
				bedroomCounts.forEach(bedroomCount => {
					const key = bedroomCount === 1 ? "1 BHK" : `${bedroomCount} BHK`;
					if (!groups[key]) groups[key] = [];
					groups[key].push(sup);
				});
			}

			if (sup.type === "sp") {
				sup.mediaList = [
					...(sup.mediaList || []).filter(media => media.name === "Floorplan"),
					...(sup.mediaList || []).filter(media => media.name !== "Floorplan"),
				];
			}
		});

		return groups;
	}, [sups]);

	const sortedGroups = useMemo(() => {
		return Object.entries(groupedSUPs).sort((a, b) => {
			// Sort by bedroom count, putting "Other" at the end
			if (a[0] === "Other") return 1;
			if (b[0] === "Other") return -1;

			const aNum = parseInt(a[0]);
			const bNum = parseInt(b[0]);
			return aNum - bNum;
		});
	}, [groupedSUPs]);

	// Set the first tab as selected by default
	const defaultSelectedKey = sortedGroups[0]?.[0] || "";

	if (!sups || sups.length === 0 || sortedGroups.length === 0) {
		return (
			<div className="my-4 bg-accent-300 text-primary p-4 mb-2 rounded-full" role="alert">
				<p className="text-center font-bold">❗ No units available for sale right now.</p>
			</div>
		);
	}

	return (
		<ErrorBoundary fallback={ProjectCardFallback}>
			<div className="w-full">
				<Tabs selectedKey={selectedTab || defaultSelectedKey} onSelectionChange={key => setSelectedTab(key as string)}>
					<TabList className="mb-6">
						{sortedGroups.map(([bedroomType]) => (
							<Tab key={bedroomType} id={bedroomType}>
								{bedroomType}
							</Tab>
						))}
					</TabList>
					{sortedGroups.map(([bedroomType, groupSUPs]) => (
						<TabPanel key={bedroomType} id={bedroomType}>
							<div className="grid grid-cols-1 @lg:grid-cols-2 @4xl:grid-cols-3 gap-4">
								{groupSUPs.map(sup => (
									<div
										key={sup._id}
										onClick={() =>
											openPropertyOverlay({
												sups: [sup],
												initialSupId: sup._id,
												projectName: sup.projectName,
											})
										}
									>
										<SUPCard data={sup} shouldRedirect={false} />
									</div>
								))}
							</div>
						</TabPanel>
					))}
				</Tabs>
			</div>
		</ErrorBoundary>
	);
}
